import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Rating,
  useMediaQuery,
} from "@mui/material";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import RateReviewOutlinedIcon from "@mui/icons-material/RateReviewOutlined";
import useTranslation from "../../../shared/hooks/useTranslation";
import { useRedesignSetup } from "../../../shared/hooks/useRedesignSetup";
import { dt, accentGradientDark, accentGradientDarkHover, accentShimmer } from "../../../shared/themes/redesignTokens";

interface LeaveReviewDialogProps {
  open: boolean;
  revieweeCompanyName: string;
  onSubmit: (rating: number, title?: string, comment?: string) => void;
  onSkip: () => void;
  onClose: () => void;
  loading?: boolean;
  error?: string;
}

const ratingLabelKeys: { [key: number]: string } = {
  1: "servicing:reviews.ratingPoor",
  2: "servicing:reviews.ratingFair",
  3: "servicing:reviews.ratingGood",
  4: "servicing:reviews.ratingVeryGood",
  5: "servicing:reviews.ratingExcellent",
};

/* -- Blue accent tokens (info / feedback action) -- */
const blue = dt.blue;
const blueDark = dt.blueHover;

/* -- Star color -- */
const starGold = "#F59E0B";
const starGoldHover = "#EAB308";

const LeaveReviewDialog = ({
  open,
  revieweeCompanyName,
  onSubmit,
  onSkip,
  onClose,
  loading = false,
  error,
}: LeaveReviewDialogProps) => {
  useRedesignSetup();
  const { translate } = useTranslation(["servicing", "common"]);
  const isPhone = useMediaQuery("(max-width:600px)");
  const [rating, setRating] = useState<number | null>(null);
  const [hover, setHover] = useState(-1);
  const [title, setTitle] = useState("");
  const [comment, setComment] = useState("");
  const [ratingError, setRatingError] = useState(false);

  // Inject dialog-specific keyframes
  useEffect(() => {
    const styleId = "leave-review-dialog-keyframes";
    if (!document.getElementById(styleId)) {
      const style = document.createElement("style");
      style.id = styleId;
      style.textContent = `
        @keyframes dialogSlideUp {
          0% { opacity: 0; transform: translateY(24px) scale(0.97); }
          100% { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes starPop {
          0% { transform: scale(1); }
          50% { transform: scale(1.25); }
          100% { transform: scale(1); }
        }
      `;
      document.head.appendChild(style);
    }
  }, []);

  const handleSubmit = () => {
    if (!rating) {
      setRatingError(true);
      return;
    }
    setRatingError(false);
    onSubmit(
      rating,
      title.trim() || undefined,
      comment.trim() || undefined
    );
  };

  const handleClose = () => {
    setRating(null);
    setHover(-1);
    setTitle("");
    setComment("");
    setRatingError(false);
    onClose();
  };

  /* -- Styled input field renderer -- */
  const renderInput = (
    label: string,
    value: string,
    onChange: (val: string) => void,
    opts: {
      placeholder: string;
      multiline?: boolean;
      rows?: number;
      maxLength: number;
    }
  ) => (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
      <Box
        sx={{
          fontSize: "13px",
          fontWeight: 600,
          color: dt.textPrimary,
        }}
      >
        {label}
      </Box>
      <Box
        sx={{
          background: dt.inputBg,
          borderRadius: dt.radiusSm,
          border: "1.5px solid transparent",
          padding: opts.multiline ? "12px" : "0 12px",
          transition: "all 0.2s ease",
          "&:focus-within": {
            border: `1.5px solid ${blue}`,
            boxShadow: `0 0 0 3px ${dt.blueGlow}`,
          },
        }}
      >
        {opts.multiline ? (
          <textarea
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={opts.placeholder}
            rows={opts.rows || 4}
            maxLength={opts.maxLength}
            style={{
              border: "none",
              outline: "none",
              width: "100%",
              fontSize: "14px",
              fontFamily: 'var(--font-sans)',
              color: dt.textPrimary,
              background: "transparent",
              resize: "vertical",
              minHeight: opts.rows ? `${opts.rows * 22}px` : "88px",
              boxSizing: "border-box",
            }}
          />
        ) : (
          <input
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={opts.placeholder}
            maxLength={opts.maxLength}
            style={{
              border: "none",
              outline: "none",
              width: "100%",
              fontSize: "14px",
              fontFamily: 'var(--font-sans)',
              color: dt.textPrimary,
              background: "transparent",
              height: "44px",
              boxSizing: "border-box",
            }}
          />
        )}
      </Box>
    </Box>
  );

  const activeRating = hover !== -1 ? hover : rating || 0;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: dt.radiusLg,
          overflow: "hidden",
          fontFamily: dt.font,
          "& *:not(.MuiSvgIcon-root)": { fontFamily: dt.font },
          animation: "dialogSlideUp 0.35s cubic-bezier(0.4, 0, 0.2, 1)",
          maxWidth: "480px",
          ...(isPhone && {
            margin: "16px",
            width: "calc(100% - 32px)",
            maxWidth: "none",
          }),
        },
      }}
    >
      {/* -- Blue gradient accent bar -- */}
      <Box
        sx={{
          height: "4px",
          background: accentShimmer,
        }}
      />

      {/* -- Header -- */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.25,
          px: 3,
          pt: 2,
          pb: 0.5,
        }}
      >
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: "50%",
            backgroundColor: dt.blueSoft,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <RateReviewOutlinedIcon sx={{ color: blue, fontSize: 20 }} />
        </Box>
        <Box
          sx={{
            fontWeight: 700,
            fontSize: { xs: "18px", sm: "20px" },
            color: dt.textPrimary,
            letterSpacing: "-0.3px",
          }}
        >
          {translate("servicing:reviews.leaveReviewTitle")}
        </Box>
      </Box>

      <DialogContent sx={{ px: 3, pt: 0.5, pb: 0 }}>
        {/* -- Subtitle -- */}
        <Box
          sx={{
            fontSize: "14px",
            color: dt.textSecondary,
            mb: 3,
            mt: 0.5,
            lineHeight: 1.6,
          }}
        >
          {translate("servicing:reviews.leaveReviewDescription", {
            company: revieweeCompanyName,
          })}
        </Box>

        {/* -- Star Rating -- */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 1,
            py: 2,
            mb: 2,
            backgroundColor: dt.inputBg,
            borderRadius: dt.radiusSm,
            border: ratingError ? "1.5px solid #EF4444" : "1.5px solid transparent",
            transition: "border-color 0.2s ease",
          }}
        >
          <Rating
            name="service-rating"
            value={rating}
            onChange={(_event, newValue) => {
              setRating(newValue);
              setRatingError(false);
            }}
            onChangeActive={(_event, newHover) => {
              setHover(newHover);
            }}
            size="large"
            icon={
              <StarIcon
                fontSize="inherit"
                sx={{
                  transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                }}
              />
            }
            emptyIcon={
              <StarBorderIcon
                fontSize="inherit"
                sx={{
                  color: dt.textMuted,
                  opacity: 0.5,
                  transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                }}
              />
            }
            sx={{
              fontSize: { xs: "42px", sm: "48px" },
              gap: { xs: "4px", sm: "8px" },
              "& .MuiRating-iconFilled": {
                color: starGold,
                filter: "drop-shadow(0 1px 2px rgba(245, 158, 11, 0.3))",
              },
              "& .MuiRating-iconHover": {
                color: starGoldHover,
                transform: "scale(1.15)",
              },
              "& .MuiRating-icon": {
                transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
              },
            }}
          />
          {activeRating > 0 && (
            <Box
              sx={{
                fontSize: "14px",
                fontWeight: 500,
                color: activeRating >= 4 ? "#059669" : activeRating >= 3 ? dt.textPrimary : "#DC2626",
                transition: "all 0.2s ease",
                minHeight: "21px",
              }}
            >
              {translate(ratingLabelKeys[activeRating])}
            </Box>
          )}
          {!activeRating && (
            <Box
              sx={{
                fontSize: "13px",
                color: dt.textMuted,
                minHeight: "21px",
              }}
            >
              {translate("servicing:reviews.ratingRequired")}
            </Box>
          )}
          {ratingError && (
            <Box
              sx={{
                fontSize: "12px",
                fontWeight: 500,
                color: "#EF4444",
              }}
            >
              {translate("servicing:reviews.ratingRequired")}
            </Box>
          )}
        </Box>

        {/* -- Title Field -- */}
        {renderInput(
          translate("servicing:reviews.reviewTitle"),
          title,
          setTitle,
          {
            placeholder: translate("servicing:reviews.reviewTitlePlaceholder"),
            maxLength: 200,
          }
        )}

        <Box sx={{ height: "16px" }} />

        {/* -- Comment Field -- */}
        {renderInput(
          translate("servicing:reviews.reviewComment"),
          comment,
          setComment,
          {
            placeholder: translate("servicing:reviews.reviewCommentPlaceholder"),
            multiline: true,
            rows: 4,
            maxLength: 4000,
          }
        )}

        {/* -- Error message -- */}
        {error && (
          <Box
            sx={{
              mt: 2,
              backgroundColor: "rgba(239, 68, 68, 0.06)",
              borderLeft: "3px solid #EF4444",
              borderRadius: `0 ${dt.radiusSm} ${dt.radiusSm} 0`,
              p: 1.5,
              fontSize: "13px",
              color: "#DC2626",
              lineHeight: 1.5,
            }}
          >
            {error}
          </Box>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          pb: 2.5,
          pt: 2,
          gap: 1,
          flexDirection: isPhone ? "column-reverse" : "row",
          "& > :not(:first-of-type)": {
            ml: isPhone ? "0 !important" : undefined,
          },
        }}
      >
        {/* Skip button */}
        <Button
          onClick={onSkip}
          disabled={loading}
          sx={{
            textTransform: "none",
            fontWeight: 600,
            fontSize: "14px",
            color: dt.textSecondary,
            borderRadius: dt.radiusSm,
            px: 2.5,
            py: 1,
            transition: "all 0.2s ease",
            width: isPhone ? "100%" : "auto",
            "&:hover": {
              backgroundColor: dt.inputBg,
              color: dt.textPrimary,
            },
          }}
        >
          {translate("servicing:reviews.skipForNow")}
        </Button>

        {/* Submit Review button (blue gradient) */}
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || !rating}
          sx={{
            background: accentGradientDark,
            borderRadius: dt.radiusPill,
            textTransform: "none",
            fontWeight: 600,
            fontSize: "14px",
            px: 3,
            py: 1,
            boxShadow: "0 2px 8px rgba(52,152,219,0.25)",
            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
            width: isPhone ? "100%" : "auto",
            whiteSpace: "nowrap",
            "&:hover": {
              background: accentGradientDarkHover,
              transform: "translateY(-2px)",
              boxShadow: "0 6px 20px rgba(52,152,219,0.3)",
            },
            "&.Mui-disabled": {
              background: "#D1D5DB",
              color: "#9CA3AF",
              boxShadow: "none",
            },
          }}
        >
          {loading
            ? translate("servicing:reviews.submitting")
            : translate("servicing:reviews.submitReview")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LeaveReviewDialog;
